import * as React from 'react';
import { useState } from 'react';

import { Button, Grid, Typography } from '@coursera/cds-core';

import DomainTopSkillPill from 'bundles/browse-common/legacy/components/Domain/DomainTopSkillPill';
import type { Skill } from 'bundles/browse-common/legacy/components/Domain/types/Skill';
import { TOTAL_NUMBER_OF_SKILLS_TO_SHOW } from 'bundles/browse/components/Domain/constants/browseDomainSkillsData';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/browse';

type Props = {
  skillsList: Array<Skill>;
  domainName: string;
  headerLabel?: string;
  subDomainName?: string;
  isMobileBrowser: boolean;
  isEntityPage?: boolean;
  entityPageUrl?: string;
};

const TOTAL_NUMBER_OF_SKILLS_TO_SHOW_DESKTOP = 6;
const TOTAL_NUMBER_OF_SKILLS_TO_SHOW__MOBILE = 4;

export const DomainTopSkills = ({
  skillsList,
  domainName,
  headerLabel,
  subDomainName,
  isMobileBrowser,
  isEntityPage,
  entityPageUrl,
}: Props) => {
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [numberOfSkillsDesktop, setNumberOfSkillsDesktop] = useState(TOTAL_NUMBER_OF_SKILLS_TO_SHOW_DESKTOP);
  const [numberOfSkillsMobile, setNumberOfSkillsMobile] = useState(TOTAL_NUMBER_OF_SKILLS_TO_SHOW__MOBILE);

  if (!skillsList.length) {
    return null;
  }

  const updateNumberOfRowsOnMobile = () => {
    setShowAllSkills(numberOfSkillsMobile + TOTAL_NUMBER_OF_SKILLS_TO_SHOW__MOBILE >= TOTAL_NUMBER_OF_SKILLS_TO_SHOW);
    setNumberOfSkillsMobile(numberOfSkillsMobile + TOTAL_NUMBER_OF_SKILLS_TO_SHOW__MOBILE);
  };

  const updateNumberOfRowsOnDesktop = () => {
    setShowAllSkills(numberOfSkillsDesktop + TOTAL_NUMBER_OF_SKILLS_TO_SHOW_DESKTOP >= TOTAL_NUMBER_OF_SKILLS_TO_SHOW);
    setNumberOfSkillsDesktop(numberOfSkillsDesktop + TOTAL_NUMBER_OF_SKILLS_TO_SHOW_DESKTOP);
  };

  const onClickShowMore = (isMobile: boolean) => {
    if (isMobile) {
      updateNumberOfRowsOnMobile();
    } else {
      updateNumberOfRowsOnDesktop();
    }
  };

  const renderDomainTopSkill = (skill: Skill, skillIndex: number): React.ReactNode => {
    const hideSkillOnMobile = skillIndex >= numberOfSkillsMobile;
    const hideSkillOnDesktop = skillIndex >= numberOfSkillsDesktop;
    const shouldHideSkill = isMobileBrowser ? hideSkillOnMobile : hideSkillOnDesktop;

    return (
      <Grid item key={skillIndex} className={shouldHideSkill ? 'shouldHide' : undefined} xs="auto">
        <DomainTopSkillPill {...skill} />
      </Grid>
    );
  };

  const renderDomainTopSkillsList = (): React.ReactNode => {
    if (isEntityPage && entityPageUrl) {
      return skillsList
        .filter(({ skillSearchUrl }) => skillSearchUrl !== entityPageUrl)
        .slice(0, TOTAL_NUMBER_OF_SKILLS_TO_SHOW)
        .map((skill, index) => renderDomainTopSkill(skill, index));
    }
    return skillsList
      .slice(0, TOTAL_NUMBER_OF_SKILLS_TO_SHOW)
      .map((skill, index) => renderDomainTopSkill(skill, index));
  };

  let domainOrSubDomainName = domainName;

  if (subDomainName) {
    domainOrSubDomainName = subDomainName;
  }

  const numberOfSkillsToShowOnPage = isMobileBrowser
    ? TOTAL_NUMBER_OF_SKILLS_TO_SHOW__MOBILE
    : TOTAL_NUMBER_OF_SKILLS_TO_SHOW_DESKTOP;

  return (
    <Grid container spacing={16} data-unit="domain-top-skills" className="rc-DomainTopSkills">
      <Grid item>
        <Typography data-unit="domain-skills-header" className="domain-skills-header" variant="h2semibold">
          {headerLabel ?? _t('Skills you can learn in #{domainOrSubDomainName}', { domainOrSubDomainName })}
        </Typography>
      </Grid>

      <Grid item container spacing={12}>
        {renderDomainTopSkillsList()}
      </Grid>

      {skillsList.length > numberOfSkillsToShowOnPage && (
        <Grid item className={showAllSkills ? 'showAllSkills' : undefined}>
          <Button
            data-unit="show-more-btn"
            variant="ghost"
            trackingName="show_more_skills"
            component={TrackedButton}
            onClick={() => onClickShowMore(isMobileBrowser)}
            withVisibilityTracking={false}
            requireFullyVisible={false}
            aria-label={_t('Show More Skills')}
            className="showMoreTrigger"
          >
            {_t('Show More')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default DomainTopSkills;
